import { useState, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

/**
 * Input a tag liberi con auto-suggerimento.
 * - Enter o virgola per aggiungere un tag
 * - click sulla X per rimuoverlo
 * - dropdown suggerimenti filtrato (esclude i tag già selezionati)
 */
export function TagInput({ value = [], onChange, suggestions = [], placeholder = 'Aggiungi tag...', testId = 'tag-input' }) {
  const [input, setInput] = useState('');
  const [focused, setFocused] = useState(false);
  const blurTimer = useRef(null);

  const lowerValues = value.map(v => v.toLowerCase());

  const addTag = (raw) => {
    const t = (raw || '').trim().replace(/,+$/, '').trim();
    if (!t) return;
    if (lowerValues.includes(t.toLowerCase())) { setInput(''); return; }
    onChange([...value, t]);
    setInput('');
  };

  const removeTag = (tag) => {
    onChange(value.filter(v => v !== tag));
  };

  const filteredSuggestions = suggestions
    .filter(s => !lowerValues.includes(s.toLowerCase()))
    .filter(s => !input.trim() || s.toLowerCase().includes(input.trim().toLowerCase()))
    .slice(0, 8);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(input);
    } else if (e.key === 'Backspace' && !input && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-tags`}>
          {value.map(tag => (
            <Badge key={tag} variant="secondary" className="text-xs font-normal pr-1 gap-1">
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="rounded-full hover:bg-black/10 p-0.5 transition-colors duration-150"
                aria-label={`Rimuovi ${tag}`}
                data-testid={`${testId}-remove-${tag}`}
              >
                <X size={11} />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <div className="relative">
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (blurTimer.current) clearTimeout(blurTimer.current); setFocused(true); }}
          onBlur={() => { blurTimer.current = setTimeout(() => { addTag(input); setFocused(false); }, 150); }}
          placeholder={placeholder}
          data-testid={testId}
        />
        {focused && filteredSuggestions.length > 0 && (
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-white shadow-lg py-1 max-h-44 overflow-y-auto" data-testid={`${testId}-suggestions`}>
            {filteredSuggestions.map(s => (
              <button
                key={s}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); addTag(s); }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-[hsl(var(--secondary))] transition-colors duration-150"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">Premi Invio o virgola per aggiungere un tag.</p>
    </div>
  );
}

export default TagInput;
